'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { X, Search, MessageCircle, Plus, Settings } from 'lucide-react'

interface Conversation {
  id: string
  title: string
  updated_at: string
  created_at: string
  categories: { name: string; icon: string }
}

interface ConversationsSidebarProps {
  isOpen: boolean
  onClose: () => void
  currentConversationId?: string
  onConversationSelect: (conversationId: string) => void
}

export default function ConversationsSidebar({
  isOpen,
  onClose,
  currentConversationId,
  onConversationSelect,
}: ConversationsSidebarProps) {
  const { user, userProfile, isLoading: authLoading } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [lastUserSnippets, setLastUserSnippets] = useState<Record<string, string>>({})
  const [isDesktop, setIsDesktop] = useState(false)

  const supabase = createClientComponentClient()

  // Track desktop breakpoint
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const fetchConversations = useCallback(async () => {
    if (!user) return
    try {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('conversations')
        .select(`id, title, updated_at, created_at, categories!inner(name, icon)`)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setConversations((data as any) || [])
      setFilteredConversations((data as any) || [])

      // Lazy-load snippets after render
      setTimeout(async () => {
        const limited = data?.slice(0, 20) || []
        const snippets: Record<string, string> = {}
        try {
          for (let i = 0; i < limited.length; i += 5) {
            await Promise.all(
              limited.slice(i, i + 5).map(async (conv: any) => {
                const { data: msg } = await supabase
                  .from('messages')
                  .select('content')
                  .eq('conversation_id', conv.id)
                  .eq('role', 'user')
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle()
                if (msg?.content) {
                  const t = String(msg.content)
                  snippets[conv.id] = t.length > 80 ? t.slice(0, 77) + '…' : t
                }
              })
            )
            setLastUserSnippets(prev => ({ ...prev, ...snippets }))
          }
        } catch { }
      }, 100)

    } catch (err) {
      console.error('Error fetching conversations:', err)
    } finally {
      setIsLoading(false)
    }
  }, [user, supabase])

  useEffect(() => { if (user) fetchConversations() }, [user, fetchConversations])

  // Realtime subscription
  useEffect(() => {
    if (!user?.id || !isOpen) return
    const ch = supabase
      .channel(`sidebar-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversations', filter: `user_id=eq.${user.id}` }, fetchConversations)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `user_id=eq.${user.id}` }, fetchConversations)
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [user?.id, isOpen, fetchConversations, supabase])

  // Filter
  useEffect(() => {
    let f = conversations
    if (searchTerm) f = f.filter(c =>
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.categories.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    if (selectedCategory !== 'all') f = f.filter(c => c.categories.name === selectedCategory)
    setFilteredConversations(f)
  }, [conversations, searchTerm, selectedCategory])

  const handleConversationClick = (cid: string) => {
    onConversationSelect(cid)
    if (window.innerWidth < 1024) onClose()
  }

  const formatTimeAgo = (d: string) => {
    try {
      const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000)
      if (mins < 1) return 'Just now'
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `${hrs}h ago`
      const days = Math.floor(hrs / 24)
      if (days < 7) return `${days}d ago`
      return new Date(d).toLocaleDateString()
    } catch { return 'N/A' }
  }

  const getCategoryBg = (name: string) => {
    const map: Record<string, string> = {
      'Health & Wellness': 'rgba(74,124,94,0.15)',
      'Faith & Spirituality': 'rgba(201,144,58,0.15)',
      'Family & Relationships': 'rgba(74,124,94,0.15)',
      'Cooking': 'rgba(201,144,58,0.15)',
      'Travel Planning': 'rgba(232,237,242,1)',
    }
    return map[name] || 'rgba(74,124,94,0.12)'
  }

  if (authLoading) return null

  // ── SIDEBAR LAYOUT STRATEGY ──────────────────────────────
  // Desktop (lg+): rendered inline in the flex row. Width is 300px when open, 0 when closed.
  //   The main content naturally fills the remaining space.
  // Mobile (<lg): rendered as a fixed overlay with a dark backdrop.
  // This avoids the "fixed sidebar overlapping main content" bug.

  const SIDEBAR_WIDTH = 300

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/*
        KEY FIX:
        - Desktop: position relative (inline in flex), width animates 0↔300px
        - Mobile: position fixed, slides in from left as overlay
        This is the correct pattern to avoid the layout-crush bug.
      */}
      <div
        style={{
          // Desktop: inline flex child
          ...(isDesktop ? {
            position: 'relative',
            width: isOpen ? `${SIDEBAR_WIDTH}px` : '0px',
            minWidth: 0,
            overflow: 'hidden',
            flexShrink: 0,
            transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
          } : {
            // Mobile: fixed overlay
            position: 'fixed',
            top: 0, left: 0, height: '100%',
            width: `${SIDEBAR_WIDTH}px`,
            maxWidth: '85vw',
            zIndex: 50,
            transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
          }),
          background: '#1a2740',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Inner content — fixed width so it doesn't squish during animation */}
        <div style={{ width: `${SIDEBAR_WIDTH}px`, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '22px 18px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <div className="flex items-center gap-3 mb-4">
              <div style={{
              }}><img src="/icons/blue-sage-logo.png" alt="Sage" style={{ width: '42px', height: '42px', objectFit: 'contain' }} /></div>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: 700, color: 'white', flex: 1 }}>Sage</span>
              {/* Mobile close button */}
              {!isDesktop && (
                <button onClick={onClose} style={{
                  width: '30px', height: '30px', background: 'rgba(255,255,255,0.08)',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* New Chat button */}
            <button
              onClick={() => { window.location.href = '/dashboard' }}
              style={{
                width: '100%', background: '#4a7c5e', border: 'none',
                borderRadius: '13px', padding: '14px 18px',
                color: 'white', fontFamily: "'Source Serif 4', serif",
                fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#2d5a3d'}
              onMouseLeave={e => e.currentTarget.style.background = '#4a7c5e'}
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              Start a New Chat
            </button>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button
                onClick={() => { window.location.href = '/dashboard' }}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.08)', border: 'none',
                  borderRadius: '10px', padding: '10px 0',
                  color: 'rgba(255,255,255,0.8)', fontFamily: "'Source Serif 4', serif",
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              >
                Dashboard
              </button>
              <button
                onClick={() => { window.location.href = '/profile' }}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.08)', border: 'none',
                  borderRadius: '10px', padding: '10px 0',
                  color: 'rgba(255,255,255,0.8)', fontFamily: "'Source Serif 4', serif",
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              >
                Profile
              </button>
              <button
                onClick={() => { window.location.href = '/settings' }}
                style={{
                  flex: '0 0 auto', width: '40px', background: 'rgba(255,255,255,0.08)', border: 'none',
                  borderRadius: '10px', padding: '10px 0',
                  color: 'rgba(255,255,255,0.8)', fontFamily: "'Source Serif 4', serif",
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              >
                <Settings className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div style={{ padding: '10px 14px 4px', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search style={{
                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                width: '13px', height: '13px', color: 'rgba(255,255,255,0.3)'
              }} />
              <input
                type="text"
                placeholder="Search conversations…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: '100%', padding: '9px 10px 9px 30px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px', color: 'rgba(255,255,255,0.85)',
                  fontSize: '13px', outline: 'none',
                  fontFamily: "'Source Serif 4', serif"
                }}
              />
            </div>
          </div>

          {/* Section label */}
          <div style={{
            padding: '14px 18px 6px',
            fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)',
            flexShrink: 0
          }}>Recent Conversations</div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}
            className="sage-sidebar-scroll">
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse"
                  style={{ padding: '12px', borderRadius: '12px', marginBottom: '4px' }}>
                  <div style={{ width: '34px', height: '34px', background: 'rgba(255,255,255,0.08)', borderRadius: '9px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: '13px', background: 'rgba(255,255,255,0.08)', borderRadius: '5px', width: '70%', marginBottom: '6px' }} />
                    <div style={{ height: '11px', background: 'rgba(255,255,255,0.05)', borderRadius: '5px', width: '50%' }} />
                  </div>
                </div>
              ))
              : filteredConversations.length === 0
                ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(255,255,255,0.3)' }}>
                    <MessageCircle style={{ width: '28px', height: '28px', margin: '0 auto 10px', opacity: 0.4 }} />
                    <p style={{ fontSize: '13px' }}>No conversations found</p>
                  </div>
                )
                : filteredConversations.map(conv => (
                  <div
                    key={conv.id}
                    onClick={() => handleConversationClick(conv.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px', borderRadius: '12px', marginBottom: '3px',
                      cursor: 'pointer',
                      background: conv.id === currentConversationId
                        ? 'rgba(74,124,94,0.22)'
                        : 'transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (conv.id !== currentConversationId) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                    onMouseLeave={e => { if (conv.id !== currentConversationId) e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '9px', flexShrink: 0,
                      background: getCategoryBg(conv.categories?.name),
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px'
                    }}>
                      {conv.categories?.icon || '💬'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.88)',
                        marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {lastUserSnippets[conv.id] || conv.title || 'New Conversation'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.32)' }}>
                        {conv.categories?.name} · {formatTimeAgo(conv.updated_at)}
                      </div>
                    </div>
                  </div>
                ))
            }
          </div>

          {/* Footer */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0
          }}>
            <div style={{
              width: '36px', height: '36px', background: '#c9903a', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', color: 'white', fontWeight: 700, flexShrink: 0
            }}>
              {user?.email?.[0]?.toUpperCase() || '👤'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userProfile?.name || user?.email?.split('@')[0] || 'User'}
              </div>
              <div style={{ fontSize: '11px', color: '#c9903a' }}>
                ⭐ {userProfile?.subscription_status === 'premium' ? 'Premium' : 'Free'} Member
              </div>
            </div>
            <button
              onClick={() => { window.location.href = '/settings' }}
              style={{
                width: '28px', height: '28px', background: 'rgba(255,255,255,0.07)',
                border: 'none', borderRadius: '7px', cursor: 'pointer',
                color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}